import { memo, useEffect, type ReactNode } from 'react';
import { PhoneCall, PhoneOff } from 'lucide-react';
import { Avatar, Button } from '@cyphex/ui';
import { startRingback, playSound } from '@/lib/sounds';
import { formatDuration } from '../../lib/format';

export interface CallRingingScreenProps {
  peerName: string;
  onCancel: () => void;
}

/**
 * The caller's screen between "I pressed call" and `call.connected`.
 *
 * The media client is ALREADY connected behind this — the caller is publishing
 * into an empty room — so the moment the callee's tracks arrive there is no
 * renegotiation, only this overlay going away.
 */
export const CallRingingScreen = memo(function CallRingingScreen(props: CallRingingScreenProps) {
  /* The caller hears it ringing - a screen that only SAYS "Ringing…" left them
     wondering whether anything was happening at all. */
  useEffect(() => startRingback(), []);
  return (
    <div className="flex h-full flex-col items-center justify-center gap-6 bg-stage p-8">
      <Avatar name={props.peerName} size="4xl" ring />
      <div className="text-center">
        <h1 className="text-lg font-semibold text-stage-foreground">{props.peerName}</h1>
        <p className="mt-1 animate-pulse text-base text-stage-muted">Ringing…</p>
      </div>
      <Button variant="danger" leftIcon={PhoneOff} onClick={props.onCancel}>
        Cancel
      </Button>
    </div>
  );
});

export interface CallEndedScreenProps {
  peerName: string;
  reason: string;
  durationMs: number | null;
  onCallAgain: () => void;
  onBack: () => void;
  /**
   * The voicemail recorder, when this call can still take one. Passed in
   * rather than built here so this file stays presentational and the
   * eligibility question is asked exactly once, by the page.
   */
  voicemail?: ReactNode;
}

/** After a hangup: what happened, how long it lasted, and one way back in. */
export const CallEndedScreen = memo(function CallEndedScreen(props: CallEndedScreenProps) {
  useEffect(() => {
    playSound('callEnded');
  }, []);
  return (
    <div className="flex h-full flex-col items-center justify-center gap-6 bg-stage p-8">
      <Avatar name={props.peerName} size="3xl" />
      <div className="text-center">
        <h1 className="text-lg font-semibold text-stage-foreground">{props.reason}</h1>
        <p className="mt-1 text-base text-stage-muted">
          {props.peerName}
          {props.durationMs === null ? '' : ` · ${formatDuration(props.durationMs)}`}
        </p>
      </div>
      {props.voicemail ?? null}

      <div className="flex gap-2">
        <Button variant="primary" leftIcon={PhoneCall} onClick={props.onCallAgain}>
          Call again
        </Button>
        <Button variant="ghost" onClick={props.onBack}>
          Back to chat
        </Button>
      </div>
    </div>
  );
});
